import {
  ArrowLeft,
  Blocks,
  Check,
  CheckCircle,
  ChevronDown,
  Copy,
  Loader2,
  Plus,
  Shield,
  Trash2,
  Users,
  Wrench,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AddSkillModal } from "@/components/extensions/AddSkillModal";
import { CredentialsList } from "@/components/extensions/CredentialsList";
import { EditSkillModal } from "@/components/extensions/EditSkillModal";
import { EditToolModal } from "@/components/extensions/EditToolModal";
import { resolveExtensionIcon } from "@/components/extensions/IconColorPickers";
import { ProfilesEditor } from "@/components/extensions/ProfilesEditor";
import { useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { extensionService, skillService, teamService } from "@/lib/services";
import type { ExtensionResponse, Team, ToolSummaryForExtension, UserOAuthSetup } from "@/types";

// ---------------------------------------------------------------------------
// Capability badge (reused from Tools page pattern)
// ---------------------------------------------------------------------------

const capabilityBadgeConfig: Record<string, { label: string; bg: string; text: string }> = {
  context: { label: "Context", bg: "bg-cyan-500/20", text: "text-cyan-400" },
  action: { label: "Action", bg: "bg-amber-500/20", text: "text-amber-400" },
  artifact: { label: "Artifact", bg: "bg-purple-500/20", text: "text-purple-400" },
};

function CapabilityBadge({ capability }: Readonly<{ capability: string }>) {
  const cfg = capabilityBadgeConfig[capability];
  if (!cfg) return null;
  return <span className={`text-xs ${cfg.bg} ${cfg.text} px-1.5 py-0.5 rounded-sm`}>{cfg.label}</span>;
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

function ToggleSwitch({
  checked,
  disabled,
  onChange,
}: Readonly<{ checked: boolean; disabled?: boolean; onChange: () => void }>) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only peer" />
      <div
        className={`w-11 h-6 bg-surface-tertiary peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:rtl:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:inset-s-[2px] after:bg-white after:border-border-hover after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 ${disabled ? "opacity-50" : ""}`}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function Section({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <div className="bg-surface-secondary/50 border border-border-secondary rounded-xl p-5">
      <h3 className="text-sm font-semibold text-content-secondary uppercase tracking-wider mb-4">{title}</h3>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-user OAuth setup instructions (admin registers the redirect URI)
// ---------------------------------------------------------------------------

function UserOAuthSetupInfo({ setup }: Readonly<{ setup: UserOAuthSetup }>) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(setup.redirectUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showToast("Could not copy to clipboard", "error");
    }
  };

  return (
    <div className="mb-4 space-y-3 text-sm">
      <p className="text-content-tertiary">
        This is a per-user OAuth integration. In the provider's developer console, create an OAuth app and register the
        exact <span className="font-medium text-content-secondary">Redirect URI</span> below for this deployment. Then
        set the app's client ID and secret in the backend environment. Once enabled, each user connects their own
        account under Settings → Integrations.
      </p>

      <div>
        <div className="text-xs font-medium text-content-secondary mb-1">Redirect URI (register this exactly)</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary">
            {setup.redirectUri}
          </code>
          <button
            type="button"
            onClick={copy}
            className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium transition-colors"
          >
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {setup.scopes.length > 0 && (
        <div>
          <div className="text-xs font-medium text-content-secondary mb-1">Requested scopes</div>
          <code className="inline-block px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary">
            {setup.scopes.join(" ")}
          </code>
          <p className="text-xs text-content-quaternary mt-1">
            Enable these scopes on the OAuth app (or use the provider's full-permissions option — then the app must not
            request specific scopes).
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function ExtensionDetail() {
  const { user } = useAuth();

  if (!user?.isPlatformAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-content-primary">Access Denied</h2>
          <p className="mt-2 text-content-tertiary max-w-sm">
            You don't have permission to view this extension. Contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return <ExtensionDetailContent />;
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

function ExtensionDetailContent() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [extension, setExtension] = useState<ExtensionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [editingSkill, setEditingSkill] = useState<import("@/types").SkillSummaryForExtension | null>(null);
  const [editingTool, setEditingTool] = useState<import("@/types").ToolSummaryForExtension | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchExtension = useCallback(async () => {
    if (!uuid) return;
    try {
      setLoading(true);
      setError(null);
      const response = await extensionService.get(uuid);
      setExtension(response.data);
    } catch (err) {
      console.error("Failed to fetch extension:", err);
      setError("Failed to load extension details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchExtension();
  }, [fetchExtension]);

  const handleToggleExtension = async () => {
    if (!extension || !uuid) return;
    try {
      setToggling(true);
      await extensionService.toggle(uuid, !extension.isEnabled);
      await fetchExtension();
    } catch (err) {
      console.error("Failed to toggle extension:", err);
    } finally {
      setToggling(false);
    }
  };

  const handleDeleteExtension = async () => {
    try {
      setDeleting(true);
      await extensionService.delete(extension!.uuid);
      showToast("Extension deleted", "success");
      navigate("/extensions");
    } catch {
      showToast("Failed to delete extension", "error");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error || !extension) {
    return (
      <div className="flex-1 flex flex-col min-h-0 p-6">
        <button
          onClick={() => navigate("/extensions")}
          className="flex items-center gap-2 text-content-tertiary hover:text-content-primary transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Extensions
        </button>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Blocks className="w-16 h-16 text-content-quaternary mx-auto mb-4" />
            <p className="text-content-tertiary text-lg">{error || "Extension not found"}</p>
          </div>
        </div>
      </div>
    );
  }

  const IconComponent = resolveExtensionIcon(extension.icon);

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-auto">
      {/* Header */}
      <div className="shrink-0 border-b border-border-primary p-6">
        <button
          onClick={() => navigate("/extensions")}
          className="flex items-center gap-2 text-content-tertiary hover:text-content-primary transition mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Extensions
        </button>

        <div className="flex items-center justify-between max-w-4xl">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                !extension.color ? "bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20" : ""
              }`}
              style={extension.color ? { backgroundColor: `${extension.color}20` } : undefined}
            >
              <IconComponent
                className={`w-6 h-6 ${!extension.color ? "text-brand-primary" : ""}`}
                style={extension.color ? { color: extension.color } : undefined}
              />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-content-primary">{extension.name}</h1>
                {extension.isSystem && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-sm">
                    System
                  </span>
                )}
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded border ${
                    extension.isEnabled
                      ? "bg-green-400/10 text-green-400 border-green-400/30"
                      : "bg-surface-tertiary/10 text-content-tertiary border-content-tertiary/30"
                  }`}
                >
                  {extension.isEnabled ? "Enabled" : "Disabled"}
                </span>
                <ToggleSwitch checked={extension.isEnabled} disabled={toggling} onChange={handleToggleExtension} />
                {toggling && <Loader2 className="w-4 h-4 text-content-tertiary animate-spin" />}
              </div>
              {extension.description && <p className="text-content-tertiary mt-1">{extension.description}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable sections */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl space-y-6">
          {extension.tools.length > 0 && (
            <Section title={`Tools (${extension.tools.length})`}>
              <ToolsList
                extension={extension}
                onEdit={(tool) => {
                  if (extension.isSystem) {
                    showToast("System tools cannot be edited", "warning");
                  } else {
                    setEditingTool(tool);
                  }
                }}
              />
            </Section>
          )}

          {extension.skills.length > 0 && (
            <Section title={`Skills (${extension.skills.length})`}>
              <SkillsList
                extension={extension}
                onUpdated={fetchExtension}
                onEdit={(skill) => {
                  setEditingSkill(skill);
                }}
              />
            </Section>
          )}

          {extension.skills.length === 0 && (
            <Section title="Skills">
              <p className="text-sm text-content-tertiary">No skills configured for this extension.</p>
            </Section>
          )}

          <div className="flex justify-end -mt-4 mb-2">
            <button
              onClick={() => setShowAddSkill(true)}
              className="flex items-center gap-1.5 text-sm text-brand-primary hover:text-brand-primary/80 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Skill
            </button>
          </div>

          {extension.authType && (
            <Section title={`Configuration (${extension.authType})`}>
              {extension.userOauthSetup && <UserOAuthSetupInfo setup={extension.userOauthSetup} />}
              <CredentialsList extension={extension} onUpdated={fetchExtension} />
            </Section>
          )}

          <Section title="Access">
            <AccessEditor extension={extension} onUpdated={fetchExtension} />
          </Section>

          {/* Profiles — per-team context/ontology customization */}
          {extension.extensionType === "integration" && (
            <Section title={`Profiles (${extension.profiles?.length || 0})`}>
              <ProfilesEditor
                extensionUuid={extension.uuid}
                profiles={extension.profiles || []}
                onUpdate={fetchExtension}
              />
            </Section>
          )}

          {!extension.isSystem && (
            <div className="mt-8 border border-red-500/30 rounded-lg p-5 bg-red-500/5">
              <h3 className="text-lg font-semibold text-red-400 mb-1">Danger Zone</h3>
              <p className="text-sm text-content-tertiary mb-4">
                Permanently delete this extension and all its configuration. Tools and skills will be unlinked but not
                deleted.
              </p>
              {showDeleteConfirm ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-red-400">Are you sure? This cannot be undone.</span>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteExtension}
                    disabled={deleting}
                    className="flex items-center gap-2 px-4 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Yes, Delete
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Extension
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddSkill && extension && (
        <AddSkillModal
          extensionUuid={extension.uuid}
          onClose={() => setShowAddSkill(false)}
          onSave={() => {
            setShowAddSkill(false);
            fetchExtension();
          }}
        />
      )}

      {editingSkill && (
        <EditSkillModal
          skill={editingSkill}
          onClose={() => setEditingSkill(null)}
          onSave={() => {
            setEditingSkill(null);
            fetchExtension();
          }}
        />
      )}

      {editingTool && (
        <EditToolModal
          tool={editingTool}
          onClose={() => setEditingTool(null)}
          onSave={() => {
            setEditingTool(null);
            fetchExtension();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tools list
// ---------------------------------------------------------------------------

function ToolsList({
  extension,
  onEdit,
}: Readonly<{ extension: ExtensionResponse; onEdit: (tool: ToolSummaryForExtension) => void }>) {
  return (
    <div className="space-y-3">
      {extension.tools.map((tool) => (
        <ToolRow key={tool.uuid} tool={tool} onEdit={() => onEdit(tool)} />
      ))}
    </div>
  );
}

function ToolRow({ tool, onEdit }: Readonly<{ tool: ToolSummaryForExtension; onEdit: () => void }>) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="w-full flex items-center justify-between bg-surface-primary border border-border-primary rounded-lg px-4 py-3 cursor-pointer hover:border-brand-primary/30 transition-colors text-left"
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 rounded-lg flex items-center justify-center">
          <Wrench className="w-5 h-5 text-brand-primary" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-content-primary">{tool.name}</h3>
            <span className="text-xs bg-surface-tertiary text-content-secondary px-2 py-0.5 rounded-sm">
              {tool.toolType}
            </span>
            {tool.capabilities.map((cap) => (
              <CapabilityBadge key={cap} capability={cap} />
            ))}
            {tool.escalation && tool.escalation !== "none" && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  tool.escalation === "required" ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                {tool.escalation === "required" ? "Confirm Required" : "Confirm Optional"}
              </span>
            )}
          </div>
          {tool.description && <p className="text-sm text-content-tertiary">{tool.description}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {tool.isEnabled ? (
          <CheckCircle className="w-5 h-5 text-green-400" />
        ) : (
          <XCircle className="w-5 h-5 text-content-quaternary" />
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Skills list
// ---------------------------------------------------------------------------

function SkillsList({
  extension,
  onUpdated,
  onEdit,
}: Readonly<{
  extension: ExtensionResponse;
  onUpdated: () => void;
  onEdit: (skill: import("@/types").SkillSummaryForExtension) => void;
}>) {
  const { showToast } = useToast();
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  const handleDeleteSkill = async (uuid: string) => {
    try {
      setDeletingUuid(uuid);
      await skillService.delete(uuid);
      showToast("Skill deleted", "success");
      onUpdated();
    } catch {
      showToast("Failed to delete skill", "error");
    } finally {
      setDeletingUuid(null);
    }
  };

  return (
    <div className="space-y-3">
      {extension.skills.map((skill) => (
        <div
          key={skill.uuid}
          onClick={() => onEdit(skill)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onEdit(skill);
          }}
          role="button"
          tabIndex={0}
          className="w-full flex items-center justify-between bg-surface-primary border border-border-primary rounded-lg px-4 py-3 cursor-pointer hover:border-brand-primary/30 transition-colors text-left"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${skill.color}20` }}
            >
              <Zap className="w-5 h-5" style={{ color: skill.color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium text-content-primary">{skill.name}</h3>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded ${
                    skill.skillType === "dynamic_session"
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : skill.skillType === "custom_container"
                        ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                        : "bg-blue-500/10 text-blue-400 border-blue-500/20"
                  }`}
                >
                  <Wrench className="w-3 h-3" />
                  {skill.skillType === "dynamic_session"
                    ? "Dynamic Session"
                    : skill.skillType === "custom_container"
                      ? "Custom Container"
                      : "Context"}
                </span>
                {skill.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-2 py-0.5 text-xs bg-surface-tertiary text-content-secondary rounded">
                    {tag}
                  </span>
                ))}
                {skill.tags.length > 3 && (
                  <span className="px-2 py-0.5 text-xs text-content-tertiary">+{skill.tags.length - 3}</span>
                )}
              </div>
              {skill.description && <p className="text-sm text-content-tertiary mt-0.5">{skill.description}</p>}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSkill(skill.uuid);
            }}
            disabled={deletingUuid === skill.uuid}
            className="ml-4 p-1.5 text-content-quaternary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 shrink-0"
            title="Delete skill"
          >
            {deletingUuid === skill.uuid ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Credentials list (with inline editing)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Access editor
// ---------------------------------------------------------------------------

function AccessEditor({
  extension,
  onUpdated,
}: Readonly<{ extension: ExtensionResponse; onUpdated: () => Promise<void> }>) {
  const isGlobal = extension.teamAccess.length === 0;

  const [accessMode, setAccessMode] = useState<"all" | "restricted">(isGlobal ? "all" : "restricted");
  const [saving, setSaving] = useState(false);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync accessMode when extension data changes (e.g., after refetch)
  useEffect(() => {
    setAccessMode(extension.teamAccess.length === 0 ? "all" : "restricted");
  }, [extension.teamAccess]);

  // Load all teams when switching to restricted mode
  useEffect(() => {
    if (accessMode === "restricted" && !teamsLoaded) {
      teamService
        .list()
        .then((res) => {
          setAllTeams(res.data);
          setTeamsLoaded(true);
        })
        .catch((err) => {
          console.error("Failed to load teams:", err);
        });
    }
  }, [accessMode, teamsLoaded]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentTeamUuids = extension.teamAccess.map((t) => t.teamUuid);

  const availableTeams = allTeams.filter((team) => !currentTeamUuids.includes(team.uuid));

  const persistAccess = async (teamUuids: string[]) => {
    try {
      setSaving(true);
      await extensionService.setTeamAccess(extension.uuid, teamUuids);
      await onUpdated();
    } catch (err) {
      console.error("Failed to update team access:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = async (mode: "all" | "restricted") => {
    setAccessMode(mode);
    if (mode === "all") {
      await persistAccess([]);
    }
  };

  const handleAddTeam = async (teamUuid: string) => {
    setDropdownOpen(false);
    await persistAccess([...currentTeamUuids, teamUuid]);
  };

  const handleRemoveTeam = async (teamUuid: string) => {
    const next = currentTeamUuids.filter((u) => u !== teamUuid);
    if (next.length === 0) {
      // Removing the last team reverts to "all"
      setAccessMode("all");
    }
    await persistAccess(next);
  };

  return (
    <div className="space-y-4">
      {/* Radio toggle */}
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-3 p-3 bg-surface-primary rounded-lg border border-border-primary cursor-pointer">
          <input
            type="radio"
            name="access-mode"
            aria-label="All teams"
            checked={accessMode === "all"}
            onChange={() => handleModeChange("all")}
            disabled={saving}
            className="accent-brand-primary"
          />
          <div>
            <p className="text-sm font-medium text-content-primary">All teams</p>
            <p className="text-xs text-content-quaternary">
              This extension is available to all teams in the organization
            </p>
          </div>
        </label>

        <label className="flex items-center gap-3 p-3 bg-surface-primary rounded-lg border border-border-primary cursor-pointer">
          <input
            type="radio"
            name="access-mode"
            aria-label="Restricted to specific teams"
            checked={accessMode === "restricted"}
            onChange={() => handleModeChange("restricted")}
            disabled={saving}
            className="accent-brand-primary"
          />
          <div>
            <p className="text-sm font-medium text-content-primary">Restricted to specific teams</p>
            <p className="text-xs text-content-quaternary">Only selected teams can use this extension</p>
          </div>
        </label>
      </div>

      {/* Team chips + add dropdown */}
      {accessMode === "restricted" && (
        <div className="space-y-3">
          {/* Current teams as removable chips */}
          {extension.teamAccess.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {extension.teamAccess.map((team) => (
                <span
                  key={team.teamUuid}
                  className="bg-surface-tertiary text-content-primary px-3 py-1.5 rounded-lg text-sm flex items-center gap-2"
                >
                  {team.teamName}
                  <button
                    onClick={() => handleRemoveTeam(team.teamUuid)}
                    disabled={saving}
                    className="text-content-quaternary hover:text-content-primary transition disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Add team dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              disabled={saving || availableTeams.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-surface-tertiary hover:bg-surface-tertiary/80 text-content-primary rounded-lg transition disabled:opacity-50"
            >
              <Users className="w-4 h-4" />
              Add team
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {dropdownOpen && availableTeams.length > 0 && (
              <div className="absolute z-10 mt-1 w-64 bg-surface-primary border border-border-primary rounded-lg shadow-lg overflow-hidden">
                <div className="max-h-48 overflow-auto">
                  {availableTeams.map((team) => (
                    <button
                      key={team.uuid}
                      onClick={() => handleAddTeam(team.uuid)}
                      className="w-full text-left px-3 py-2 text-sm text-content-primary hover:bg-surface-secondary transition"
                    >
                      {team.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Saving indicator */}
          {saving && (
            <div className="flex items-center gap-2 text-xs text-content-tertiary">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Saving...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
